import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TrendingUp, Users, Send, CheckCircle2, DollarSign, ArrowLeft, Building2, Activity, Calendar, ExternalLink, Trash2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { adminApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const Stat = ({ label, value, sub, icon: Icon, highlight }) => (
  <div className={`relative border rounded-sm p-6 bg-white ${highlight ? 'border-[#4b5320]/50' : 'border-stone-200/80'}`}>
    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#4b5320] to-transparent"></div>
    <div className="flex items-start justify-between mb-3">
      <div className="text-xs uppercase tracking-widest text-stone-500">{label}</div>
      <Icon className="w-4 h-4 text-[#4b5320]" strokeWidth={1.8} />
    </div>
    <div className={`font-display text-4xl ${highlight ? 'text-[#4b5320]' : 'text-stone-900'}`}>{value}</div>
    {sub && <div className="text-xs text-stone-500 mt-2">{sub}</div>}
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    active: { label: 'Active', cls: 'border-[#4b5320]/40 bg-[#4b5320]/10 text-[#4b5320]' },
    trialing: { label: 'Trialing', cls: 'border-blue-300 bg-blue-50 text-blue-700' },
    past_due: { label: 'Past Due', cls: 'border-amber-300 bg-amber-50 text-amber-700' },
    canceled: { label: 'Canceled', cls: 'border-stone-300 bg-stone-100 text-stone-600' },
    incomplete: { label: 'Incomplete', cls: 'border-stone-300 bg-stone-100 text-stone-600' },
  };
  const s = map[status] || { label: 'No sub', cls: 'border-stone-300 bg-stone-100 text-stone-500' };
  return <span className={`inline-block text-xs uppercase tracking-widest px-2 py-0.5 rounded-sm border ${s.cls}`}>{s.label}</span>;
};

const fmtMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString() : '—';
const fmtDateTime = (s) => s ? new Date(s).toLocaleString() : '—';

// Tiny SVG line chart
const LineChart = ({ data, valueKey, height = 80, color = '#4b5320', label }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data.map(d => d[valueKey] || 0));
  const w = 100;
  const h = 100;
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1 || 1)) * w;
    const y = h - ((d[valueKey] || 0) / max) * h;
    return `${x},${y}`;
  }).join(' ');
  const area = `0,${h} ${pts} ${w},${h}`;
  return (
    <div>
      {label && <div className="text-xs text-stone-500 mb-2">{label}</div>}
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }} className="block">
        <polygon points={area} fill={color} opacity="0.12" />
        <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
};

const Admin = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [activity, setActivity] = useState([]);
  const [signups, setSignups] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const [o, c, a, s, r] = await Promise.all([
        adminApi.overview(),
        adminApi.companies(),
        adminApi.activity(30),
        adminApi.signups(30),
        adminApi.revenue(30),
      ]);
      setOverview(o.data);
      setCompanies(c.data);
      setActivity(a.data);
      setSignups(s.data);
      setRevenue(r.data);
    } catch (e) {
      if (e.response?.status === 403) {
        setForbidden(true);
      } else {
        toast({ title: 'Could not load admin data', description: e.response?.data?.detail || 'Try again' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDeleteCompany = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await adminApi.deleteCompany(confirmDelete.id);
      toast({ title: 'Company deleted', description: `${confirmDelete.company || confirmDelete.email} and all data removed.` });
      setConfirmDelete(null);
      await load();
    } catch (e) {
      // 404 means the company was already deleted (stale row) - refresh the list silently
      if (e.response?.status === 404) {
        toast({ title: 'Already deleted', description: 'This company was already removed. Refreshing the list.' });
        setConfirmDelete(null);
        await load();
      } else {
        toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Try again.' });
      }
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-stone-500">Loading admin console...</div>;
  }
  if (forbidden) {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center">
        <div className="border border-stone-200/80 bg-white rounded-sm p-10">
          <h1 className="font-display text-3xl text-stone-900 mb-3">Access Denied</h1>
          <p className="text-sm text-stone-600 mb-6">This area is restricted to the platform owner.</p>
          <Link to="/app" className="inline-flex items-center gap-2 text-sm text-[#4b5320] hover:text-[#3d4419]">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-2">Command Center</div>
          <h1 className="font-display text-4xl text-stone-900">Admin Console</h1>
          <p className="text-sm text-stone-600 mt-2">Platform-wide metrics, MRR, and company activity.</p>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Monthly Recurring Revenue" value={fmtMoney(overview.mrrCents)} sub={`${overview.companiesPaying} paying companies`} icon={DollarSign} highlight />
        <Stat label="Total Companies" value={overview.totalCompanies} sub={`${overview.companiesPastDue} past due · ${overview.companiesCanceled} canceled`} icon={Building2} />
        <Stat label="Total Clients" value={overview.totalClients} sub={`${overview.billableClients} billable across platform`} icon={Users} />
        <Stat label="Total Disputes" value={overview.totalDisputes} sub={`${overview.itemsRemoved} items removed`} icon={CheckCircle2} />
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="border border-stone-200/80 bg-white rounded-sm p-6">
          <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Last 30 days</div>
          <h3 className="font-display text-xl text-stone-900 mb-4">Daily Signups</h3>
          <LineChart data={signups} valueKey="count" color="#4b5320" />
          <div className="flex justify-between text-xs text-stone-500 mt-2">
            <span>{signups[0]?.date}</span>
            <span>Total: {signups.reduce((s, d) => s + d.count, 0)}</span>
            <span>{signups[signups.length - 1]?.date}</span>
          </div>
        </div>
        <div className="border border-stone-200/80 bg-white rounded-sm p-6">
          <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Last 30 days</div>
          <h3 className="font-display text-xl text-stone-900 mb-4">New Recurring Revenue</h3>
          <LineChart data={revenue} valueKey="newRevenueCents" color="#4b5320" />
          <div className="flex justify-between text-xs text-stone-500 mt-2">
            <span>{revenue[0]?.date}</span>
            <span>Total new: {fmtMoney(revenue.reduce((s, d) => s + d.newRevenueCents, 0))}</span>
            <span>{revenue[revenue.length - 1]?.date}</span>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Companies table */}
        <div className="lg:col-span-2 border border-stone-200/80 bg-white rounded-sm">
          <div className="p-6 border-b border-stone-200/80">
            <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Roster</div>
            <h2 className="font-display text-xl text-stone-900">Companies ({companies.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200/80 text-xs uppercase tracking-widest text-stone-500">
                  <th className="text-left px-5 py-3 font-medium">Company</th>
                  <th className="text-left px-3 py-3 font-medium">Status</th>
                  <th className="text-right px-3 py-3 font-medium">Clients</th>
                  <th className="text-right px-3 py-3 font-medium">Billable</th>
                  <th className="text-right px-3 py-3 font-medium">MRR</th>
                  <th className="text-left px-3 py-3 font-medium">Signed up</th>
                  <th className="text-right px-3 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200/80">
                {companies.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-stone-500">No companies signed up yet.</td>
                  </tr>
                )}
                {companies.map(c => (
                  <tr
                    key={c.id}
                    className="hover:bg-stone-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/app/admin/companies/${c.id}`)}
                  >
                    <td className="px-5 py-3">
                      <div className="text-stone-900 font-medium">{c.company || '—'}</div>
                      <div className="text-xs text-stone-500">{c.name} · {c.email}</div>
                    </td>
                    <td className="px-3 py-3"><StatusPill status={c.subscriptionStatus} /></td>
                    <td className="px-3 py-3 text-right font-mono text-stone-800">{c.totalClients}</td>
                    <td className="px-3 py-3 text-right font-mono text-stone-800">{c.billableClients}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#4b5320] font-semibold">{fmtMoney(c.mrrCents)}</td>
                    <td className="px-3 py-3 text-stone-600 text-xs whitespace-nowrap">{fmtDate(c.createdAt)}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/app/admin/companies/${c.id}`); }}
                        className="inline-flex items-center gap-1 text-xs text-stone-700 hover:text-[#4b5320] mr-3"
                        title="View"
                      >
                        View <ArrowRight className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(c); }}
                        className="inline-flex items-center text-stone-400 hover:text-red-600 transition-colors"
                        title="Delete company"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Activity feed */}
        <div className="border border-stone-200/80 bg-white rounded-sm">
          <div className="p-6 border-b border-stone-200/80">
            <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Field Report</div>
            <h2 className="font-display text-xl text-stone-900">Platform Activity</h2>
          </div>
          <div className="divide-y divide-stone-200/80 max-h-[600px] overflow-y-auto">
            {activity.length === 0 && <div className="p-8 text-center text-stone-500 text-sm">No activity yet.</div>}
            {activity.map(a => (
              <div key={a.id} className="px-5 py-3">
                <div className="text-sm text-stone-800">{a.message}</div>
                <div className="text-xs text-stone-500 mt-1">
                  <span className="text-stone-700">{a.companyName || a.companyEmail}</span> · <span className="text-stone-500">{a.clientName}</span>
                </div>
                <div className="text-xs text-stone-400 mt-0.5">{fmtDateTime(a.createdAt)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Delete confirm dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="bg-white border-stone-200 text-stone-900 rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-red-700">Delete Company</DialogTitle>
          </DialogHeader>
          {confirmDelete && (
            <div className="text-sm text-stone-700 space-y-3">
              <p>You're about to permanently delete <strong>{confirmDelete.company || confirmDelete.email}</strong> and all of their data.</p>
              <div className="bg-stone-100 border border-stone-200 rounded-sm p-3 text-xs space-y-1">
                <div>• {confirmDelete.totalClients} clients</div>
                <div>• {confirmDelete.totalDisputes} disputes</div>
                <div>• All uploaded documents</div>
                <div>• Active Stripe subscription will be canceled immediately</div>
              </div>
              <p className="text-red-700 text-xs font-semibold">This cannot be undone.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} className="border-stone-300 bg-white text-stone-700 hover:bg-stone-100 rounded-sm">Cancel</Button>
            <Button onClick={handleDeleteCompany} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white font-semibold rounded-sm">
              <Trash2 className="w-4 h-4 mr-2" /> {deleting ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
